import { create } from "zustand";

import { clearLaunchAppendWaiter } from "@/lib/launch/launchAppendCoordinator";
import {
  LAUNCH_HANDOFF_TIMEOUT_MS,
  emptyLaunchSurrogatePreview,
  isValidLaunchHandoffRect,
  type LaunchHandoffMode,
  type LaunchHandoffRect,
  type LaunchHandoffSurrogatePreview,
} from "@/lib/launch/launchHandoff";
import {
  canTransitionLaunchPhase,
  type LaunchHandoffPhase,
} from "@/lib/launch/launchHandoffPhases";

const IDLE_STATE = {
  phase: "idle" as LaunchHandoffPhase,
  conversationId: null as string | null,
  fromRect: null as LaunchHandoffRect | null,
  toRect: null as LaunchHandoffRect | null,
  mode: "none" as LaunchHandoffMode,
  surrogate: emptyLaunchSurrogatePreview(),
  composerHidden: false,
};

function transitionPhase(
  set: (partial: Partial<LaunchHandoffStore>) => void,
  get: () => LaunchHandoffStore,
  next: LaunchHandoffPhase,
) {
  const current = get().phase;
  if (!canTransitionLaunchPhase(current, next)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[launchHandoff] invalid phase transition ${current} -> ${next}`);
    }
    return false;
  }
  set({ phase: next });
  return true;
}

let safetyTimer: ReturnType<typeof setTimeout> | null = null;

function clearSafetyTimer() {
  if (safetyTimer != null) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
}

function armSafetyTimer(get: () => LaunchHandoffStore) {
  clearSafetyTimer();
  safetyTimer = setTimeout(() => {
    const state = get();
    if (state.phase === "idle" || state.phase === "ready") return;
    if (process.env.NODE_ENV !== "production") {
      console.warn("[launchHandoff] safety timeout; forcing ready");
    }
    get().markReady();
  }, LAUNCH_HANDOFF_TIMEOUT_MS);
}

interface LaunchHandoffStore {
  phase: LaunchHandoffPhase;
  conversationId: string | null;
  fromRect: LaunchHandoffRect | null;
  toRect: LaunchHandoffRect | null;
  mode: LaunchHandoffMode;
  surrogate: LaunchHandoffSurrogatePreview;
  /** Hides the real workbench composer while the flight surrogate is visible. */
  composerHidden: boolean;

  beginPreparing: (args: {
    fromRect: LaunchHandoffRect | null;
    mode: LaunchHandoffMode;
    previewText?: string;
    fileNames?: string[];
    contextLabels?: string[];
  }) => void;
  updateSurrogate: (patch: Partial<LaunchHandoffSurrogatePreview>) => void;
  beginNavigating: (args: { conversationId: string }) => void;
  registerDestinationRect: (toRect: LaunchHandoffRect) => void;
  completeFlight: () => void;
  markReady: () => void;
  reset: () => void;
  abort: () => void;

  /** @deprecated Use phase machine actions instead. */
  begin: (args: {
    conversationId: string;
    fromRect: LaunchHandoffRect | null;
    mode: LaunchHandoffMode;
  }) => void;
  /** @deprecated Use {@link reset} after drain completes. */
  complete: () => void;
}

export const useLaunchHandoffStore = create<LaunchHandoffStore>((set, get) => ({
  ...IDLE_STATE,

  beginPreparing: ({ fromRect, mode, previewText = "", fileNames = [], contextLabels = [] }) => {
    clearSafetyTimer();
    set({
      ...IDLE_STATE,
      phase: "preparing",
      fromRect: mode === "none" ? null : fromRect,
      mode,
      surrogate: {
        previewText,
        fileNames,
        contextLabels,
        uploading: false,
        uploadProgress: {},
      },
      composerHidden: mode === "motion",
    });
    armSafetyTimer(get);
  },

  updateSurrogate: (patch) => {
    set((state) => ({
      surrogate: { ...state.surrogate, ...patch },
    }));
  },

  beginNavigating: ({ conversationId }) => {
    if (!transitionPhase(set, get, "navigating")) return;
    set({ conversationId });
    const { mode } = get();
    if (mode === "none") {
      get().markReady();
    }
  },

  registerDestinationRect: (toRect) => {
    if (!isValidLaunchHandoffRect(toRect)) return;
    const state = get();
    if (state.phase !== "navigating" || state.mode !== "motion") return;
    set({ toRect });
    if (transitionPhase(set, get, "flying")) {
      armSafetyTimer(get);
    }
  },

  completeFlight: () => {
    const state = get();
    if (state.phase !== "flying") return;
    if (!transitionPhase(set, get, "settling")) return;
    set({ composerHidden: false });
    window.setTimeout(() => {
      if (get().phase === "settling") {
        get().markReady();
      }
    }, 80);
  },

  markReady: () => {
    clearSafetyTimer();
    const state = get();
    if (state.phase === "idle") return;
    set({
      phase: "ready",
      composerHidden: false,
    });
  },

  reset: () => {
    clearSafetyTimer();
    const conversationId = get().conversationId;
    if (conversationId) {
      clearLaunchAppendWaiter(conversationId);
    }
    set({ ...IDLE_STATE });
  },

  abort: () => {
    const conversationId = get().conversationId;
    if (conversationId) {
      clearLaunchAppendWaiter(conversationId);
    }
    clearSafetyTimer();
    set({ ...IDLE_STATE });
  },

  begin: ({ conversationId, fromRect, mode }) => {
    get().beginPreparing({ fromRect, mode });
    get().beginNavigating({ conversationId });
    if (mode === "none") {
      get().markReady();
    }
  },

  complete: () => {
    get().reset();
  },
}));

/** True while launch continuity is in flight for a conversation (suppress welcome, etc.). */
export function isLaunchHandoffInFlightForConversation(
  phase: LaunchHandoffPhase,
  conversationId: string | null,
  targetConversationId: string,
): boolean {
  if (phase === "idle" || phase === "ready") return false;
  return conversationId === targetConversationId;
}
